import { useId, useState } from 'react';

// Number fields on the Billing page that a person types into. The old fields snapped to their minimum on every key
// press (clear a 1, type 3, get 13 packs or $10,050), so these keep the text as typed, check it when the field is left,
// and say what will be charged beside it.

const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dollars = (n: number) => `$${n.toLocaleString('en-US')}`;

/** The whole number typed, or null when the text is empty, not a whole number, or outside min..max. */
export function wholeNumber(text: string, min: number, max: number): number | null {
  const t = text.trim().replace(/[$,\s]/g, '');
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= min && n <= max ? n : null;
}

/** Packs of credits above this ask once more before the invoice is made. */
export const BIG_ORDER_PACKS = 10;

/** Credit packs: how many, the credits and the total beside the field, and a second click above 10 packs. */
export function PackField({ unitUsd, creditsPerPack, disabled, busy, onBuy, maxPacks = 50 }: {
  unitUsd: number; creditsPerPack: number; disabled: boolean; busy: boolean; onBuy: (packs: number) => void; maxPacks?: number;
}) {
  const id = useId();
  const [text, setText] = useState('1');
  const [seen, setSeen] = useState(false);
  const [sure, setSure] = useState(false);
  const n = wholeNumber(text, 1, maxPacks);
  const bad = seen && n === null;
  const credits = n ? n * creditsPerPack : 0;
  const total = n ? n * unitUsd : 0;
  const press = () => {
    setSeen(true);
    if (n === null) return;
    if (n > BIG_ORDER_PACKS && !sure) { setSure(true); return; }
    setSure(false);
    onBuy(n);
  };
  return (
    <div className="pack-field">
      <label htmlFor={`${id}-packs`}>Packs of {creditsPerPack.toLocaleString('en-US')} credits ({money(unitUsd)} each)</label>{' '}
      <input id={`${id}-packs`} type="text" inputMode="numeric" autoComplete="off" value={text} aria-invalid={bad || undefined} aria-describedby={`${id}-sum`} style={{ maxWidth: 90 }}
        onChange={(e) => { setText(e.target.value); setSure(false); }} onBlur={() => setSeen(true)} />{' '}
      <span id={`${id}-sum`} className={bad ? 'error small' : 'small muted'} role={bad ? 'alert' : undefined}>
        {n ? `= ${credits.toLocaleString('en-US')} credits for ${money(total)}` : `Enter a whole number from 1 to ${maxPacks}`}
      </span>
      <div className="actions" style={{ marginTop: 8 }}>
        {sure && n ? (
          <>
            <span className="small">That is {credits.toLocaleString('en-US')} credits for {money(total)}. </span>
            <button type="button" className="btn btn-primary" disabled={disabled || busy} onClick={press}>{busy ? 'Preparing…' : `Yes, buy ${n} packs for ${money(total)}`}</button>
            <button type="button" className="btn" onClick={() => setSure(false)}>Change the number</button>
          </>
        ) : (
          <button type="button" className="btn btn-primary" disabled={disabled || busy || n === null} onClick={press}>
            {busy ? 'Preparing…' : n ? `Buy ${credits.toLocaleString('en-US')} credits for ${money(total)}` : 'Buy credits'}
          </button>
        )}
      </div>
    </div>
  );
}

export const WALLET_CHIPS = [100, 250, 500, 1000];

/** The ad wallet amount: typed freely, checked on leaving the field, with the minimum under it and quick amounts. */
export function WalletAmount({ minUsd = 100, maxUsd = 50_000, initial, disabled, busy, onAdd }: {
  minUsd?: number; maxUsd?: number; initial?: number; disabled: boolean; busy: boolean; onAdd: (usd: number) => void;
}) {
  const id = useId();
  const [text, setText] = useState(String(initial && initial >= minUsd ? Math.min(maxUsd, initial) : Math.max(minUsd, 200)));
  const [seen, setSeen] = useState(false);
  const n = wholeNumber(text, minUsd, maxUsd);
  const bad = seen && n === null;
  return (
    <div className="pick-account wallet-amount">
      <div className="field">
        <label htmlFor={`${id}-amt`}>Amount (USD)</label>
        <input id={`${id}-amt`} type="text" inputMode="numeric" autoComplete="off" value={text} aria-invalid={bad || undefined} aria-describedby={`${id}-min`} style={{ maxWidth: 140 }}
          onChange={(e) => setText(e.target.value)} onBlur={() => setSeen(true)} />
        <span id={`${id}-min`} className={bad ? 'error small' : 'small muted'} role={bad ? 'alert' : undefined}>
          Minimum {dollars(minUsd)}{bad ? `: enter ${dollars(minUsd)} to ${dollars(maxUsd)} in whole dollars` : ''}
        </span>
      </div>
      <div className="pack-chips" role="group" aria-label="Quick amounts">
        {WALLET_CHIPS.filter((c) => c >= minUsd && c <= maxUsd).map((c) => (
          <button key={c} type="button" className={`btn btn-sm${n === c ? ' btn-primary' : ''}`} aria-pressed={n === c} onClick={() => { setText(String(c)); setSeen(true); }}>{dollars(c)}</button>
        ))}
      </div>
      <button type="button" className="btn btn-primary" disabled={disabled || busy || n === null} onClick={() => { setSeen(true); if (n !== null) onAdd(n); }}>{busy ? 'Preparing…' : n ? `Add ${money(n)}` : 'Add to the wallet'}</button>
    </div>
  );
}
